import React, { useEffect, ReactNode } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { Rocket } from 'lucide-react';

const BrandSection = () => {
  const { scrollYProgress } = useScroll();
  
  const AnimatedBlock = ({ children }: { children: ReactNode }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { 
      once: true, 
      amount: 0.3,
      margin: "-50px"
    });
    
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {children}
      </motion.div>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-groviyo-blue-light to-white -z-10" />
      
      <div className="absolute inset-0 -z-5">
        <div className="absolute top-20 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-groviyo-yellow rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
        <div className="absolute bottom-20 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-groviyo-blue rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.03)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] -z-1" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {/* Header */}
        <AnimatedBlock>
          <div className="max-w-3xl">
            <motion.div 
              className="flex items-center gap-3 mb-4 sm:mb-6"
              whileHover={{ x: 10 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-groviyo-yellow" />
              </motion.div>
              <h2 className="text-xl sm:text-2xl font-bold text-groviyo-yellow">GROVIYO</h2>
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-3 sm:mb-4">
              We help your brand
            </h1>
            <p className="text-2xl sm:text-3xl md:text-5xl font-bold text-groviyo-yellow leading-tight">
              grow online & keep customers happy.
            </p>
          </div>
        </AnimatedBlock>

        {/* Divider */}
        <AnimatedBlock>
          <motion.div 
            className="my-12 sm:my-20 w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"
            initial={{ scaleX: 0, opacity: 0 }}
            whileInView={{ scaleX: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          />
        </AnimatedBlock>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center">
          <AnimatedBlock>
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
                Full-service digital marketing
              </h2>
              <p className="text-base sm:text-xl text-gray-600 leading-relaxed">
                We'll cultivate your online presence, so you can enjoy the sweetest results. At <span className="font-semibold text-groviyo-yellow">Groviyo</span>, we've combined 50 years of real-world sales and digital marketing experience to make online growth simple for businesses like yours.
              </p>
              <motion.button 
                className="w-full sm:w-auto group relative px-6 sm:px-8 py-3 sm:py-4 bg-groviyo-yellow text-white rounded-full text-base sm:text-lg font-medium hover:shadow-lg overflow-hidden"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative">Learn More</span>
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{ x: ["100%", "-100%"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
              </motion.button>
            </div>
          </AnimatedBlock>

          {/* Image */}
          <AnimatedBlock>
            <motion.div 
              className="relative aspect-square mt-8 sm:mt-0"
              whileHover={{ scale: 1.02 }}
            >
              <motion.div 
                className="absolute inset-0 bg-gradient-to-br from-groviyo-yellow/10 to-groviyo-blue/10 rounded-2xl"
                whileHover={{ rotate: 6, scale: 1.05 }}
                initial={{ rotate: 3 }}
              />
              <motion.div 
                className="absolute inset-0 bg-white rounded-2xl"
                whileHover={{ rotate: -6, scale: 1.02 }}
                initial={{ rotate: -3 }}
              />
              <motion.div className="relative h-full rounded-2xl overflow-hidden border border-gray-200">
                <img 
                  src="/mainpage.jpg" 
                  alt="Digital Marketing" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </motion.div>
            </motion.div>
          </AnimatedBlock>
        </div>
      </div>
    </div>
  );
};

export default BrandSection;